import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';
import { toast } from 'sonner';

import { getAccessToken } from '@/api/token';
import { voiceErrorMessage } from '@/lib/voice-error-copy';
import { voiceApi } from '@/api/services/voice.api';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';
import type { IUser } from '@/features/auth/interfaces';

import {
    acceptPending,
    cancelOutboundExpectation,
    connectOutboundLeg,
    destroySessionDevice,
    endLocalLeg,
    hangupActive,
    hasActiveCall,
    initSessionDevice,
    rejectPending,
    sendDigits as sendDigitsToActiveCall,
} from '../device/session-device';
import { toggleActiveMute } from './toggle-active-mute';
import { createVoiceSocket, type VoiceSocket } from '../ws/socket-client';
import { useVoiceStore, type IActiveCall } from '../state/voice-ui.store';
import { voiceKeys } from '../query-keys';
import { noteServerTime, serverNow } from '../utils/clock-offset';
import {
    VOICE_WS_EVENT_NAMES,
    type CallStatus,
    type ICallEndedPayload,
    type ICallIncomingPayload,
    type ICallParkedPayload,
    type ICallStatePayload,
    type ICallUnparkedPayload,
    type IInitiateCallInput,
    type IInitiateCallResult,
    type IPresenceChangedPayload,
    type IRecordingReadyPayload,
    type IVoiceChannelIdentity,
    type IVoiceContactSnapshot,
    type IVoiceNumberSummary,
    type IVoiceWsEnvelope,
    type IVoicemailNewPayload,
} from '../types';

/**
 * The caller ID to actually dial with, or `undefined` for "Auto" (let the
 * server pick).
 *
 * The selected caller ID is persisted in localStorage and outlives the line it
 * names: retire the number, drop it from the SMS messaging-service scope, or
 * turn voice off for it, and the saved id is still there on the next visit.
 * Every UI that shows the selection already reconciles it against the loaded
 * list and renders "Auto" when it no longer matches — so sending the raw saved
 * id makes the request disagree with what the agent is looking at, and the
 * server rejects an explicit pick (VOICE_NUMBER_NOT_ENABLED) where "Auto"
 * would have found them a working line. Reconcile here, at the one choke point
 * every dial path goes through.
 *
 * Only drops the id once the list has actually loaded: with no list we cannot
 * tell stale from unknown, and guessing would silently ignore a real choice.
 */
function usableCallerId(
    myNumbers: readonly IVoiceNumberSummary[] | undefined,
    fromIdentityId: string | undefined,
): string | undefined {
    if (!fromIdentityId || !myNumbers) return fromIdentityId;
    return myNumbers.some((n) => n.channelIdentityId === fromIdentityId)
        ? fromIdentityId
        : undefined;
}

/**
 * Line snapshot for an outbound call — from the cached "my numbers" list,
 * preferring the explicitly selected caller ID, else matching the number the
 * server actually dialed from (its fallback chain may pick a line we know).
 */
function resolveOutboundLine(
    myNumbers: readonly IVoiceNumberSummary[] | undefined,
    fromIdentityId: string | undefined,
    fromNumber: string,
): IVoiceChannelIdentity | null {
    const line =
        myNumbers?.find((n) => n.channelIdentityId === fromIdentityId) ??
        myNumbers?.find((n) => n.identity === fromNumber);
    if (!line) return null;
    return { id: line.channelIdentityId, identity: line.identity, displayName: line.displayName };
}

/** WS state payloads carry no line info — `previous` keeps the local-only
 *  bits (mute, line snapshot) across authoritative overwrites, and the
 *  queued incoming payload backfills the line when `previous` is empty. */
function toActiveCall(
    payload: ICallStatePayload,
    previous: IActiveCall | null,
    fallbackChannelIdentity: IVoiceChannelIdentity | null,
): IActiveCall {
    return {
        callId: payload.callId,
        direction: payload.direction,
        status: payload.status,
        contact: payload.contact,
        channelIdentity: previous?.channelIdentity ?? fallbackChannelIdentity,
        fromNumber: payload.fromNumber,
        toNumber: payload.toNumber,
        answeredAt: payload.answeredAt,
        onHold: payload.onHold,
        muted: previous?.muted ?? false,
        recordingEnabled: payload.recordingEnabled,
        transferCount: payload.transferCount,
    };
}

/**
 * The single integration point between the Twilio Voice SDK, the `/voice`
 * WebSocket, and the Zustand widget store. Mount exactly once (inside
 * `CallWidget`, itself mounted once in `AppLayout`) — mirrors how
 * `useNotificationsSocket` is called once from the always-mounted
 * `NotificationBell`.
 *
 * Division of responsibility:
 *  - The SDK's own Call events drive purely local concerns (mute, and the
 *    immediate "answered locally" transition) so the UI never waits on a
 *    round trip for things only the browser knows.
 *  - The WS `voice:call:state` / `voice:call:ended` events are the
 *    authoritative source for call status — they overwrite the optimistic
 *    local guess as soon as they arrive.
 *  - `trackedCallIdRef` filters the org-wide WS broadcast down to only the
 *    ACTIVE call this browser session owns; ringing-queue entries are
 *    matched against the store's `ringingQueue` instead.
 *
 * Finish-first queue: every `voice:call:incoming` enqueues; exactly one
 * call can be active. A queued entry leaves the queue when this agent
 * answers it (promoted), declines it, the caller/timeout cancels the SDK
 * leg (backend `cancelSiblingAgentLegs` covers answered-elsewhere), or an
 * org-room WS event says it was answered by a teammate or ended — every
 * removal path is idempotent, so overlaps are harmless.
 */
export function useVoiceSession() {
    const queryClient = useQueryClient();
    const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
    const canUseVoice = hasAnyPermission([
        PERMISSION.VOICE_CALLS_MAKE,
        PERMISSION.VOICE_CALLS_RECEIVE,
    ]);

    const trackedCallIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (permissionsLoading || !canUseVoice) return;
        const token = getAccessToken();
        if (!token) return;

        let socket: VoiceSocket | null = null;
        const seen = new Set<string>();
        // Every envelope funnels through here, so this doubles as the
        // clock-offset probe: `eventAt` is the server's clock, and call
        // timestamps are server-stamped, so the elapsed-time maths has to be
        // done in that domain rather than the browser's (see clock-offset.ts).
        const alreadySeen = (
            envelope: { readonly eventId?: string; readonly eventAt?: string } | undefined,
        ): boolean => {
            noteServerTime(envelope?.eventAt);
            const eventId = envelope?.eventId;
            if (!eventId) return false;
            if (seen.has(eventId)) return true;
            seen.add(eventId);
            return false;
        };

        void initSessionDevice({
            onRegistered: () => useVoiceStore.getState().setDeviceStatus('ready'),
            onUnregistered: () => useVoiceStore.getState().setDeviceStatus('offline'),
            onError: (error) => {
                console.error('[voice] device error', error);
                toast.error('Voice connection error — reconnecting…');
            },
            // SDK ringing is only the media backing; the WS `voice:call:incoming`
            // event drives the queue UI. Nothing to do here.
            onIncoming: () => undefined,
            onCallEvent: (callId, event) => {
                const state = useVoiceStore.getState();
                switch (event.type) {
                    // Leg gone before this agent acted — caller hung up, ring
                    // timeout, or a teammate answered (sibling-cancel). For an
                    // active call, the authoritative end is the WS ended event;
                    // removing a (non-existent) queue row is a no-op.
                    case 'cancel':
                    case 'reject':
                    case 'disconnect':
                        state.removeRinging(callId);
                        return;
                    case 'accept': {
                        trackedCallIdRef.current = callId;
                        // Outbound/held-pick legs already populated the store in
                        // dial()/pickHeldCall() — only promote queued inbound.
                        const current = state.activeCall;
                        if (current?.callId === callId) return;
                        const queued = state.ringingQueue.find((q) => q.callId === callId) ?? null;
                        // setActiveCall also removes the answered queue entry.
                        state.setActiveCall({
                            callId,
                            direction: 'INBOUND',
                            status: 'IN_PROGRESS' satisfies CallStatus,
                            contact: queued?.contact ?? null,
                            channelIdentity: queued?.channelIdentity ?? null,
                            fromNumber: queued?.fromNumber ?? '',
                            toNumber: queued?.toNumber ?? '',
                            // Server clock, not the browser's: the
                            // authoritative answeredAt that replaces this one
                            // moments later is server-stamped, and mixing the
                            // two domains made the timer jump on answer.
                            answeredAt: new Date(serverNow()).toISOString(),
                            onHold: false,
                            muted: false,
                            recordingEnabled: false,
                            transferCount: 0,
                        });
                        return;
                    }
                    case 'mute':
                        state.patchActiveCall(callId, { muted: event.muted });
                        return;
                }
            },
        }).catch((error: unknown) => {
            // `initSessionDevice` arms its own backoff retry (including for a
            // failed token mint, which happens before either device module
            // holds handlers and so cannot self-heal) — just surface it.
            console.error('[voice] failed to initialize device', error);
            // A 4xx names a real configuration problem the user can act on
            // ("voice not enabled", "re-enable after switching provider"), and
            // retrying will not fix it. Anything else — network, 5xx — really
            // is transient, so leave the reason null and let the banner say
            // "reconnecting".
            const status = axios.isAxiosError(error) ? error.response?.status : undefined;
            const reason =
                status !== undefined && status >= 400 && status < 500
                    ? (voiceErrorMessage((error as AxiosError).response?.data) ??
                      'Voice calling is unavailable for your organization.')
                    : null;
            useVoiceStore.getState().setDeviceStatus('offline', reason);
        });

        socket = createVoiceSocket();

        socket.on(
            VOICE_WS_EVENT_NAMES.CALL_INCOMING,
            (envelope: IVoiceWsEnvelope<ICallIncomingPayload>) => {
                if (alreadySeen(envelope)) return;
                useVoiceStore.getState().enqueueRinging(envelope.payload);
            },
        );

        socket.on(
            VOICE_WS_EVENT_NAMES.CALL_STATE,
            (envelope: IVoiceWsEnvelope<ICallStatePayload>) => {
                if (alreadySeen(envelope)) return;
                const payload = envelope.payload;
                const state = useVoiceStore.getState();
                const queued = state.ringingQueue.find((q) => q.callId === payload.callId) ?? null;

                const relevantToActive =
                    trackedCallIdRef.current === payload.callId ||
                    state.activeCall?.callId === payload.callId;
                if (relevantToActive) {
                    trackedCallIdRef.current = payload.callId;
                    state.setActiveCall(
                        toActiveCall(payload, state.activeCall, queued?.channelIdentity ?? null),
                    );
                    return;
                }

                // Answered elsewhere — a teammate owns it now; drop the
                // ringing entry (the SDK cancel of our leg does this too,
                // both paths are idempotent).
                if (queued && payload.status === 'IN_PROGRESS') {
                    const myId = queryClient.getQueryData<IUser>(['user', 'me'])?.id;
                    if (payload.userId !== null && payload.userId !== myId) {
                        state.removeRinging(payload.callId);
                    }
                }
            },
        );

        socket.on(
            VOICE_WS_EVENT_NAMES.CALL_ENDED,
            (envelope: IVoiceWsEnvelope<ICallEndedPayload>) => {
                if (alreadySeen(envelope)) return;
                const payload = envelope.payload;
                const state = useVoiceStore.getState();

                // An ended outbound must retire its device auto-answer
                // expectation, or a stale one could swallow the next
                // header-less inbound agent leg (Telnyx; Twilio no-op).
                cancelOutboundExpectation(payload.callId);
                // And any SDK leg still alive for this call must be hung up
                // here: when the far end hangs up first, nothing else tears
                // down the browser leg (observed as zombie Telnyx legs that
                // held the credential and blocked later inbound rings).
                endLocalLeg(payload.callId);

                // A still-ringing call ended (missed / voicemail / caller
                // gave up) — it just leaves the queue.
                const wasQueued = state.ringingQueue.some((q) => q.callId === payload.callId);
                if (wasQueued) state.removeRinging(payload.callId);
                // A parked caller whose call ended (hung up, or expired to
                // voicemail) leaves the hold queue too — idempotent.
                state.removeHeld(payload.callId);

                const relevant =
                    trackedCallIdRef.current === payload.callId ||
                    state.activeCall?.callId === payload.callId;
                if (!relevant && !wasQueued) return;
                if (relevant) {
                    trackedCallIdRef.current = null;
                    state.setActiveCall(null);
                    if (payload.status === 'COMPLETED') {
                        state.setPostCallCardCallId(payload.callId);
                    }
                }
                void queryClient.invalidateQueries({ queryKey: voiceKeys.calls.all });
            },
        );

        socket.on(
            VOICE_WS_EVENT_NAMES.VOICEMAIL_NEW,
            (envelope: IVoiceWsEnvelope<IVoicemailNewPayload>) => {
                if (alreadySeen(envelope)) return;
                void queryClient.invalidateQueries({ queryKey: voiceKeys.recordings.all });
                void queryClient.invalidateQueries({ queryKey: voiceKeys.calls.all });
            },
        );

        socket.on(
            VOICE_WS_EVENT_NAMES.RECORDING_READY,
            (envelope: IVoiceWsEnvelope<IRecordingReadyPayload>) => {
                if (alreadySeen(envelope)) return;
                void queryClient.invalidateQueries({ queryKey: voiceKeys.recordings.all });
                // Refresh the call detail too so an open post-call card flips
                // from "Processing recording…" to the player once it lands.
                void queryClient.invalidateQueries({
                    queryKey: voiceKeys.calls.detail(envelope.payload.callId),
                });
            },
        );

        socket.on(
            VOICE_WS_EVENT_NAMES.PRESENCE_CHANGED,
            (envelope: IVoiceWsEnvelope<IPresenceChangedPayload>) => {
                if (alreadySeen(envelope)) return;
                void queryClient.invalidateQueries({ queryKey: voiceKeys.presence() });
            },
        );

        // Hold queue — parked callers are broadcast org-wide so any online
        // agent can pick one up; unpark removes it (picked, hung up, expired).
        socket.on(
            VOICE_WS_EVENT_NAMES.CALL_PARKED,
            (envelope: IVoiceWsEnvelope<ICallParkedPayload>) => {
                if (alreadySeen(envelope)) return;
                useVoiceStore.getState().enqueueHeld(envelope.payload);
            },
        );

        socket.on(
            VOICE_WS_EVENT_NAMES.CALL_UNPARKED,
            (envelope: IVoiceWsEnvelope<ICallUnparkedPayload>) => {
                if (alreadySeen(envelope)) return;
                useVoiceStore.getState().removeHeld(envelope.payload.callId);
            },
        );

        socket.connect();

        return () => {
            socket?.disconnect();
            destroySessionDevice();
            // This cleanup also runs on a mid-session deps change, not just
            // unmount — drop queued rows so none outlive the SDK legs the
            // just-destroyed device was backing them with.
            useVoiceStore.getState().clearRingingQueue();
            useVoiceStore.getState().clearHeldQueue();
            useVoiceStore.getState().setDeviceStatus('connecting');
        };
    }, [canUseVoice, permissionsLoading, queryClient]);

    const dial = useCallback(
        async (
            input: IInitiateCallInput,
            // The caller (e.g. ContactDialer) already knows who it is dialing —
            // pass the snapshot so the widget labels the call with a name
            // instead of bare digits. The API result carries only `contactId`.
            options?: { contact?: IVoiceContactSnapshot },
        ): Promise<IInitiateCallResult> => {
            // Client-generated id: the in-call screen renders NOW and the
            // server creates the row under the same id, so the widget never
            // sits on the idle keypad for the whole initiate round trip
            // (which reads as "the call button lags"). Tracking from click
            // time also closes the gap where an early WS state event for
            // this call would not have matched anything yet.
            const callId = input.callId ?? crypto.randomUUID();
            const myNumbers = queryClient.getQueryData<IVoiceNumberSummary[]>(
                voiceKeys.myNumbers(),
            );
            const fromIdentityId = usableCallerId(myNumbers, input.fromIdentityId);
            trackedCallIdRef.current = callId;
            useVoiceStore.getState().setActiveCall({
                callId,
                direction: 'OUTBOUND',
                status: 'INITIATED',
                contact: options?.contact ?? null,
                channelIdentity: resolveOutboundLine(myNumbers, fromIdentityId, ''),
                fromNumber: '',
                toNumber: input.toNumber ?? '',
                answeredAt: null,
                onHold: false,
                muted: false,
                recordingEnabled: false,
                transferCount: 0,
            });
            let result: IInitiateCallResult;
            try {
                result = await voiceApi.initiateCall({ ...input, fromIdentityId, callId });
            } catch (error) {
                // Roll back only our own optimistic entry — an inbound answer
                // may have promoted a different call while the dial was in
                // flight, and that one must survive.
                const state = useVoiceStore.getState();
                if (state.activeCall?.callId === callId) state.setActiveCall(null);
                if (trackedCallIdRef.current === callId) trackedCallIdRef.current = null;
                throw error;
            }
            if (useVoiceStore.getState().activeCall?.callId !== callId) {
                // The agent pressed End while the round trip was in flight —
                // the widget could only dismiss locally (no row existed yet).
                // The row exists NOW: end it server-side, never connect a leg.
                trackedCallIdRef.current = null;
                void voiceApi.endCall(result.callId).catch(() => undefined);
                return result;
            }
            // Reconcile in place (same id → InCallPanel and its timer never
            // remount). `result.callId` normally equals ours; trusting the
            // server's copy keeps webhook/WS matching correct even against a
            // backend that ignored the client id.
            trackedCallIdRef.current = result.callId;
            useVoiceStore.getState().patchActiveCall(callId, {
                callId: result.callId,
                fromNumber: result.fromNumber,
                toNumber: result.toNumber,
                channelIdentity: resolveOutboundLine(
                    myNumbers,
                    input.fromIdentityId,
                    result.fromNumber,
                ),
            });
            await connectOutboundLeg(result.callId);
            if (useVoiceStore.getState().activeCall?.callId !== result.callId) {
                // Ended while the browser leg was connecting (isDialing was
                // still true, so End dismissed locally) — tear down both sides.
                trackedCallIdRef.current = null;
                endLocalLeg(result.callId);
                void voiceApi.endCall(result.callId).catch(() => undefined);
            }
            return result;
        },
        [queryClient],
    );

    /**
     * Pick a caller off the hold queue (finish-first — only while idle).
     * Mirrors `dial`: claim the call on the backend, then originate the browser
     * leg the backend bridges into the held caller's conference. A held call has
     * no live incoming Device call, so it cannot use `acceptPending`.
     */
    const pickHeldCall = useCallback(async (callId: string): Promise<void> => {
        if (hasActiveCall()) return;
        const held = useVoiceStore.getState().heldQueue.find((h) => h.callId === callId) ?? null;
        let result: IInitiateCallResult;
        try {
            result = await voiceApi.pickHeldCall(callId);
        } catch {
            // Grabbed by a teammate, expired to voicemail, or the caller hung up.
            useVoiceStore.getState().removeHeld(callId);
            toast.error('That call was already picked up or is no longer waiting.');
            return;
        }
        trackedCallIdRef.current = result.callId;
        useVoiceStore.getState().setActiveCall({
            callId: result.callId,
            direction: 'INBOUND',
            status: 'IN_PROGRESS',
            contact: held?.contact ?? null,
            channelIdentity: held?.channelIdentity ?? null,
            fromNumber: result.fromNumber,
            toNumber: result.toNumber,
            // Server clock, not the browser's: the authoritative answeredAt
            // that replaces this one moments later is server-stamped, and
            // mixing the two domains made the timer jump on answer.
            answeredAt: new Date(serverNow()).toISOString(),
            onHold: false,
            muted: false,
            recordingEnabled: false,
            transferCount: 0,
        });
        await connectOutboundLeg(result.callId);
    }, []);

    /**
     * Finish-first: answering is only possible while no call is active.
     * Gated on the device's SYNCHRONOUS active call, not the store's — the
     * store is written in the async SDK 'accept' handler and lags, so two
     * quick answers would both read a null store value and double-promote.
     * `acceptPending` self-guards too and its store mutation only runs off
     * a real `.accept()`, so a refused second answer is a complete no-op.
     */
    const answerIncoming = useCallback((callId: string) => {
        if (hasActiveCall()) return;
        acceptPending(callId);
    }, []);

    /** Declining a queued call works at any time, also while on a call. */
    const declineIncoming = useCallback((callId: string) => {
        rejectPending(callId);
        useVoiceStore.getState().removeRinging(callId);
    }, []);

    const hangupLocal = useCallback(() => {
        hangupActive();
    }, []);

    const toggleMute = useCallback(() => {
        toggleActiveMute();
    }, []);

    const sendDigits = useCallback((digits: string) => {
        sendDigitsToActiveCall(digits);
    }, []);

    return {
        dial,
        pickHeldCall,
        answerIncoming,
        declineIncoming,
        hangupLocal,
        toggleMute,
        sendDigits,
    };
}
